/**
 * Push Notification helpers (browser side).
 * Subscribes the user to Web Push using VAPID, posts the subscription to the
 * backend. Safe to call multiple times — backend upserts by endpoint.
 */
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

export function isPushSupported() {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

export function getPermissionState() {
  if (!isPushSupported()) return 'unsupported';
  return Notification.permission; // 'default' | 'granted' | 'denied'
}

/**
 * Subscribe (or resubscribe) the current device to push notifications.
 * Returns the subscription on success, throws on failure.
 * Times out after 10s so a broken SW never hangs the caller.
 */
export async function subscribeToPush() {
  if (!isPushSupported()) throw new Error('Push not supported in this browser');
  if (!VAPID_PUBLIC_KEY) throw new Error('VAPID public key missing');

  // Race SW-ready against a 10s timeout — broken SW shouldn't hang forever
  const reg = await Promise.race([
    navigator.serviceWorker.ready,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Service worker not ready (timeout)')), 10000)
    ),
  ]);

  // If already subscribed, refresh server record and exit
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
    });
  }

  const json = sub.toJSON();
  await axios.post(`${API}/notifications/subscribe`, {
    endpoint: json.endpoint,
    keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
    user_agent: navigator.userAgent,
  });

  return sub;
}

/**
 * Ask the user for notification permission, then subscribe.
 * Returns 'granted' | 'denied' | 'default'.
 */
export async function requestPermissionAndSubscribe() {
  if (!isPushSupported()) return 'unsupported';
  const permission = await Notification.requestPermission();
  if (permission === 'granted') {
    try {
      await subscribeToPush();
    } catch (e) {
      console.warn('Push subscribe failed', e);
    }
  }
  return permission;
}

export async function unsubscribeFromPush() {
  if (!isPushSupported()) return;
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (sub) {
    try {
      await axios.post(`${API}/notifications/unsubscribe`, { endpoint: sub.endpoint });
    } catch (e) { /* ignore */ }
    await sub.unsubscribe();
  }
}

/** Re-subscribe automatically when SW signals subscription change. */
export function attachResubscribeListener() {
  if (!isPushSupported()) return;
  navigator.serviceWorker.addEventListener('message', (event) => {
    if (event.data && event.data.type === 'RESUBSCRIBE_PUSH') {
      subscribeToPush().catch(() => {});
    }
  });
}
